import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Block } from '../classes/Block';
import { VideoSettings } from './VideoSettings';
import { t } from '@lingui/core/macro';
import { useProject } from '../contexts/ProjectContext';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { getApiUrl } from '../utils/api';
import { ApiBlock, ApiPayload } from '../utils/apiTypes';

interface VideoExporterProps {
  columnDefs: any[];
  sheetName: string;
  blocks: Block[];
  isEnabled: boolean;
  videoSettings: VideoSettings;
}
// Mock API function for create_render
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const mockCreateRender = async (data: any, projectId: string): Promise<{ status: string; render: { version: number } }> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Return mock response matching real API structure
  return { status: 'OK', render: { version: 29 } };
};

const realCreateRender = async (data: any, projectId: string): Promise<{ status: string; render: { version: number } }> => {
  const response = await fetch(`${getApiUrl()}/new-render?projectId=${projectId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    throw new Error('Failed to create render');
  }
  const result = await response.json();
  return result;
};

// Active API function - switch between mock and real
// const createRender = mockCreateRender;
const createRender = realCreateRender;

// ============================================================================

const VideoExporter: React.FC<VideoExporterProps> = ({ 
  columnDefs, 
  sheetName,
  blocks,
  isEnabled,
  videoSettings
}) => {
  const { selectedProject } = useProject();
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [renderVersion, setRenderVersion] = useState<number | null>(null);
  const popupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const transformBlockToApiFormat = useCallback(
    (block: Block): ApiBlock => {
      const rows: string[][] = [];
      if (block.dataRows && block.dataRows.length > 0) {
        for (const dataRow of block.dataRows) {
          const rowValues: string[] = [];
          for (const colDef of columnDefs) {
            let value: string = String(dataRow[colDef.field] || '');
            
            // Special handling for logo blocks: ensure col2 is JSON encoded with scale key
            if (value && block.isLogoBlock && colDef.field === 'col2') {
              try {
                // If value is already a JSON string, do nothing
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _ = JSON.parse(value);
              }
              catch {
                const scaleValue: number = parseFloat(value);
                value = JSON.stringify({ scale: isNaN(scaleValue) ? 100 : scaleValue });
              }
              
            }
            rowValues.push(value);
          }
          rows.push([...rowValues]);
        }
      }
      return {
        name: block.name,
        properties: {
          name: block.name,
          show_block_name: block.shouldDisplayBlockName,
          block_name_font_style: block.blockNameFont,
          block_name_font_size: block.blockNameSize,
          block_name_font_weight: block.blockNameWeight,
          block_name_color: block.blockNameColor,
          column_num: block.numColumns,
          distance_between_columns: block.marginColumn,
          value_label_gap: block.labelValueGap,
          distance_between_rows: block.distanceBetweenRows,
          distance_from_previous: block.distanceFromPreviousBlock,
          song_block: block.isSongBlock,
          single_cell: block.isSingleCell,
          credits_list: block.isCreditsList,
          credits_list_separator: block.separator,
          logo_block: block.isLogoBlock,
          label_font_name: block.labelFontName,
          label_font_size: block.labelFontSize,
          label_font_weight: block.labelFontWeight,
          label_align: block.labelAlign,
          label_vertical_align: block.labelVerticalAlign,
          label_color: block.labelFontColor,
          value_font_name: block.valueFontName,
          value_font_size: block.valueFontSize,
          value_font_weight: block.valueFontWeight,
          value_align: block.valueAlign,
          value_vertical_align: block.valueVerticalAlign,
          value_color: block.valueFontColor,
          has_separate_value_formatting: block.hasSeparateValueFormatting,
        },
        rows: rows,
      };
    },
    [columnDefs]
  );

  const exportToHTML = async () => {
    setIsLoading(true);
    try {
      const apiBlocks: ApiBlock[] = blocks.map((block) => transformBlockToApiFormat(block));

      const payload: ApiPayload = {
        video_settings: {
          background_color: videoSettings.background_color,
          fps_num: videoSettings.fps_num,
          video_width: videoSettings.video_width,
          transparent: videoSettings.transparent,
          video_time: videoSettings.video_time,
          aspect_ratio: videoSettings.aspect_ratio,
          video_format: videoSettings.video_format,
          fonts: JSON.stringify(videoSettings.fonts),
          logos: JSON.stringify(videoSettings.logos),
        },
        data: apiBlocks,
      };

      // Create render
      const response = await createRender(payload, selectedProject!.id);
      
      // Validate response structure
      if (!response || !response.render || typeof response.render.version !== 'number') {
        throw new Error('Invalid response from server: missing version number');
      }
      
      const version: number = response.render.version;
      setRenderVersion(version);
      setShowPopup(true);

      // Clear any existing timeout
      if (popupTimeoutRef.current) {
        clearTimeout(popupTimeoutRef.current);
      }

      // Auto-close popup after 5 seconds
      popupTimeoutRef.current = setTimeout(() => {
        setShowPopup(false);
        popupTimeoutRef.current = null;
      }, 5000);

    } catch (error) {
      console.error('Error creating render:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to create render: ${errorMessage}\nPlease try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (popupTimeoutRef.current) {
        clearTimeout(popupTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div className="html-exporter" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button 
          onClick={exportToHTML} 
          disabled={!isEnabled || isLoading}
          className="export-button"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            position: 'relative'
          }}
        >
          {isLoading && (
            <div 
              className="loading-spinner"
              style={{
                width: '16px',
                height: '16px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            />
          )}
          {isLoading ? t`Creating MP4...` : t`Create MP4`}
        </button>

        {/* Tooltip Icon */}
        <div className="tooltip-wrapper" style={{paddingLeft: '3px'}}>
          <span className="tooltip-icon">?</span>
          <span className="tooltip-text" style={{ paddingBottom: '2px' }}>
            {t`Remember to save the excel before downloading the video`}
          </span>
        </div>
      </div>

      {/* Popup notification */}
      {showPopup && renderVersion !== null && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#4CAF50',
            color: 'white',
            padding: '20px 40px',
            borderRadius: '8px',
            fontSize: '18px',
            fontWeight: 'bold',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            zIndex: 9999,
            textAlign: 'center'
          }}
        >
          Render version {renderVersion} started
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default VideoExporter;
